/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { createElement } from 'react';
import { PipeTable } from '../../react-components/markdown/MarkdownContent';
import { getMarkdownParts } from '../../react-components/markdown/markdownContentUtils';
import type { AgentMessage } from '../types';
import type {
  AgentWorkspaceWidgetSource,
  ExpandedAgentArtifact,
  ExpandedAgentArtifactKind,
} from './types';

/** Every artifact of `kind` a message can expand, in the order its expand buttons render. */
const listParts = (
  message: AgentMessage,
  kind: ExpandedAgentArtifactKind,
): ExpandedAgentArtifact[] => {
  switch (kind) {
    case 'plot':
      return (message.plots ?? []).map((value) => ({ kind, value }));
    case 'mapView':
      return (message.mapViews ?? []).map((value) => ({ kind, value }));
    case 'map':
      return (message.artifacts ?? []).map((value) => ({ kind, value }));
    case 'code':
      return getMarkdownParts(message.content).code.map(({ content, language }) => ({
        kind,
        value: { title: `Expanded ${language} code`, content, language },
      }));
    case 'markdown':
      return getMarkdownParts(message.content).markdown.map((content) => ({
        kind,
        value: { title: 'Expanded markdown', content },
      }));
    case 'table':
      return getMarkdownParts(message.content).tables.map((source) => ({
        kind,
        value: { title: 'Expanded table', content: createElement(PipeTable, source), source },
      }));
    default: {
      const unknownKind: never = kind;
      return unknownKind;
    }
  }
};

/** What identifies an artifact across renders; undefined when it can't be found again. */
const artifactKey = (artifact: ExpandedAgentArtifact): string | undefined => {
  switch (artifact.kind) {
    case 'table':
      return artifact.value.source && JSON.stringify(artifact.value.source);
    case 'map':
      return artifact.value.id;
    case 'code':
      return JSON.stringify([artifact.value.content, artifact.value.language]);
    case 'markdown':
      return artifact.value.content;
    case 'plot':
    case 'mapView':
      return JSON.stringify(artifact.value);
    default: {
      const unknownArtifact: never = artifact;
      return unknownArtifact;
    }
  }
};

/** Finds the message and position an expanded artifact came from, newest message first. */
export const findArtifactSource = (
  messages: ReadonlyArray<AgentMessage>,
  artifact: ExpandedAgentArtifact,
): AgentWorkspaceWidgetSource | undefined => {
  const key = artifactKey(artifact);
  if (key === undefined) return undefined;
  for (const message of [...messages].reverse()) {
    if (message.type !== 'assistant' || !message.turnId) continue;
    const partIndex = listParts(message, artifact.kind).findIndex(
      (part) => artifactKey(part) === key,
    );
    if (partIndex >= 0)
      return { turn_id: message.turnId, block_id: message.id, part_index: partIndex };
  }
  return undefined;
};

/** Rebuilds a saved widget's artifact from its turn's messages; undefined when it's gone. */
export const resolveArtifactSource = (
  messages: ReadonlyArray<AgentMessage>,
  kind: ExpandedAgentArtifactKind,
  { block_id, part_index }: AgentWorkspaceWidgetSource,
): ExpandedAgentArtifact | undefined => {
  const message = messages.find(({ id }) => id === block_id);
  return message ? listParts(message, kind)[part_index] : undefined;
};
